import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Form } from '@hologram-dimension/form';
import { Link } from '@hologram-dimension/link';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { TextareaField } from '@hologram-dimension/textarea-field';
import useBulkSendSmsCall from 'api/bulkActions/useBulkSendSmsCall';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import type { HandlersBulkMessageSimsResponse } from 'types/__generated__/SimInventoryApi';
import type { HologramSimIdsMap, OnSubmit } from 'types/common';
import styles from './BulkSendSmsMessage.module.css';

interface BulkSendSmsMessageProps {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims?: Array<HologramSimId>) => void;
  onError: (error?: string) => void;
}

interface BulkSendSmsFormProps {
  message: string;
  fromNumber?: string;
}

const BulkSendSmsMessage = forwardRef<OnSubmit, BulkSendSmsMessageProps>(
  ({ sims, onProcessing, onDone, onError }, ref) => {
    const doBulkSendSmsMessage = useBulkSendSmsCall();
    const [showBanner, setShowBanner] = useState(false);

    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<BulkSendSmsFormProps>({
      mode: 'onSubmit',
      shouldFocusError: true,
      reValidateMode: 'onChange',
    });

    const commitStateChange = useCallback<SubmitHandler<BulkSendSmsFormProps>>(
      async (data) => {
        onProcessing(true);
        let result: HandlersBulkMessageSimsResponse | undefined;
        try {
          result = await doBulkSendSmsMessage({
            body: data.message,
            fromNumber: data.fromNumber,
            sims,
          });

          if (result?.success) {
            onDone([...sims.keys()]);
          } else {
            onError(result?.error);
          }
        } catch (ex) {
          if (typeof ex === 'string') {
            onError(ex);
          } else {
            onError(`An error occurred: ${ex}`);
            // eslint-disable-next-line no-console
            console.error(ex);
          }
        } finally {
          onProcessing(false);
        }
      },
      [doBulkSendSmsMessage, onDone, onError, onProcessing, sims]
    );

    const onSubmit = handleSubmit(commitStateChange);

    const formRef = useRef<HTMLFormElement>(null);

    const manualSubmit = useCallback(async () => {
      formRef.current?.requestSubmit();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: manualSubmit,
      }),
      [manualSubmit]
    );

    let messageError: string | undefined;
    if (errors.message) {
      if (errors.message.message && errors.message.message.length > 0) {
        messageError = errors.message.message;
      } else {
        messageError = 'The maximum message length is 160 characters.';
      }
    }

    return (
      <Form onSubmit={onSubmit} ref={formRef}>
        <TextareaField
          label="Message"
          helpText={
            <span>
              Send a free message to your SIM.{' '}
              <Link
                type="inline"
                onClick={(e) => {
                  e.preventDefault();
                  setShowBanner((prev) => !prev);
                }}
              >
                Learn about sending a message
              </Link>
            </span>
          }
          invalid={errors.message !== undefined}
          validationMessage={messageError}
          {...register('message', { required: 'Message is required', maxLength: 160 })}
        />
        {showBanner && (
          <BannerNotification variant="note">
            <span className={styles.notificationText}>
              All SMS messages <em>to</em> Hologram SIMs are <strong>free</strong>. SMS rates only
              apply to messages sent outbound <em>from</em> your device.
            </span>
            <p className={styles.notificationText}>
              Messages sent from your dashboard or our REST API, by default, will contain the
              &quot;from number&quot; of our internal servers. When your device responds to this
              number, the message will be inserted into your data logs. To allow your device to
              respond to a different number, be sure to configure the &quot;From number&quot; below.
              Alternatively, you can{' '}
              <Link
                type="inline"
                href="https://www.hologram.io/contact-sales/"
                target="_blank"
                rel="noreferrer"
              >
                contact sales
              </Link>{' '}
              to purchase a phone number for your device and text that directly from a phone (i.e.
              without using the dashboard or API).
            </p>
          </BannerNotification>
        )}
        <TextInputField
          label="From number"
          optional
          helpText="Customize the SMS sender, if you need your device to respond to a specific destination."
          {...register('fromNumber')}
        />
      </Form>
    );
  }
);

export default BulkSendSmsMessage;
